import { captureEvent } from "@Light/app/analytics";
import { Hyperlink } from "@Light/components/page/Hyperlink";
import { ChevronDown } from "@untitledui/icons";
import clsx from "clsx";
import { useState, type ReactNode } from "react";

export type RateFaq = {
  question: string;
  answer: ReactNode;
};

export const providerFaq = (companyName: string): RateFaq => ({
  question: "Who provides the plan service?",
  answer: (
    <>
      {companyName} partners with Light, an energy provider, to provide this
      bespoke electricity plan.{" "}
      <Hyperlink
        href="https://www.poweredbylight.com/for-consumers"
        target="_blank"
        onClick={() => {
          captureEvent("learn_more_opened");
        }}
      >
        Learn more
      </Hyperlink>
    </>
  ),
});

export type RateFaqsProps = {
  faqs: RateFaq[];
};

export const RateFaqs: React.FC<RateFaqsProps> = ({ faqs }) => {
  const [openQuestions, setOpenQuestions] = useState<Set<string>>(new Set());

  return (
    <div className="flex flex-col gap-xs">
      <div className="text-xs px-lg text-tertiary">Common questions</div>
      {faqs.map(({ question, answer }) => {
        const isOpen = openQuestions.has(question);
        return (
          <div key={question} className="flex flex-col">
            <button
              className="group flex gap-xs items-center justify-between text-left px-lg py-sm text-tertiary hover:text-primary"
              onClick={() => {
                setOpenQuestions((prev) => {
                  const newSet = new Set(prev);
                  if (newSet.has(question)) {
                    newSet.delete(question);
                  } else {
                    newSet.add(question);
                  }
                  return newSet;
                });
              }}
            >
              <div className="text-xs font-medium">{question}</div>
              <ChevronDown
                className={clsx(
                  "size-3 stroke-[2.5px] shrink-0 text-fg-quaternary group-hover:text-tertiary transition-transform duration-300",
                  isOpen && "rotate-180",
                )}
              />
            </button>
            <div
              className={clsx(
                "grid transition-[grid-template-rows] duration-300 ease-in-out",
                isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
              )}
            >
              <div className="overflow-hidden">
                <div className="text-xs text-tertiary px-lg pb-md">
                  {answer}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
